// Tax Invoices — ZYNX-CRM-SPEC §4.2 (Sales Panel).
//
// Official GST Tax Invoices generated from approved proforma invoices
// or milestone-based payment schedules. Goes through Superadmin/Accounts
// approval, then issued to client. Supports payment recording.
//
// Workflow: Quotation → Proforma Invoice → Tax Invoice (Approved → Issued → Paid)
// Running Bills (stage-wise) and Final Tax Invoice (full contract) both listed here.

import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Receipt,
  FileText,
  CheckCircle2,
  Clock,
  ArrowLeft,
  Search,
  Download,
  Eye,
  Check,
  X,
  Layers,
  AlertCircle,
  Calendar,
  DollarSign,
  RefreshCw,
  CreditCard,
  PlusCircle,
  Building2,
  XCircle,
  Send,
  ChevronDown,
  ShieldCheck,
  Undo2,
  Coins,
  Wallet,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { useUser } from '@/shared/hooks/useAuth';
import { useCompanyProfile } from '@/features/recruitment/api';
import { useDocumentMasterData } from '@/shared/hooks/useDocumentMasterData';
import { AccountingSubNav } from '@/components/sales/AccountingSubNav';
import {
  SalesDocumentWorkspaceDrawer,
  type DocumentWorkspaceData,
} from '@/components/sales/SalesDocumentWorkspaceDrawer';
import { generateFullTaxInvoiceHtml } from '@/utils/sales/quotationTemplateTokens';
import { applyRunningBill } from '@/utils/sales/runningBillInvoice';
import { applyFinalInvoice } from '@/utils/sales/finalInvoiceDocument';
import { preparedByOf } from '@/utils/sales/preparedBy';
import {
  useInvoiceRequests,
  useInvoiceRequestActions,
  type InvoiceRequest,
} from '@/hooks/sales/useInvoiceRequests';
import { StatTile } from '@/features/hr/components/StatTile';
import { cn, cardClassName } from '@jira-clone/ui';

type TaxInvoiceTab = 'all' | 'pending_approval' | 'approved' | 'revision_needed' | 'paid' | 'draft';
type DocumentTypeFilter = 'all' | 'tax_invoice' | 'running_bill';

interface UnifiedTaxInvoice {
  id: string;
  invoiceNumber: string;
  date?: string;
  dueDate?: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientAddress?: string;
  clientGstin?: string;
  sourceQuotationNumber?: string;
  sourceQuotationId?: string;
  sourceProformaNumber?: string;
  sourceProformaId?: string;
  milestoneIndex?: number;
  milestonePhaseLabel?: string;
  milestoneName?: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  status: string;
  rawRequest?: InvoiceRequest;
  scheduleId?: string;
  milestoneId?: string;
  preparedBy?: string;
  items?: any[];
  notes?: string;
  rejectionReason?: string;
  revisionNote?: string;
  rejectedBy?: string;
  rejectedAt?: string;
}

export const isInvoiceRunningBill = (inv: UnifiedTaxInvoice): boolean => {
  if ((inv.rawRequest as any)?.isRunningBill) return true;
  if ((inv.rawRequest as any)?.requestType === 'running_bill') return true;
  if (inv.invoiceNumber && /(?:^|\/)RB(?:$|\/|-)/i.test(inv.invoiceNumber)) return true;
  return false;
};

export function TaxInvoicesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useUser();
  const { data: companyProfile } = useCompanyProfile();
  const { data: masterData } = useDocumentMasterData();
  const defaultBankDetail = (masterData?.bankDetails ?? []).find((b) => b.isDefault && b.isActive);

  const userRole = (currentUser?.role || '').toLowerCase();
  const userDept = (currentUser?.department || '').toLowerCase();
  const userEmail = (currentUser?.email || '').toLowerCase();
  const isSuperAdmin =
    userRole === 'superadmin' ||
    userRole === 'owner' ||
    userDept === 'superadmin' ||
    userEmail.includes('uzef');
  const isAccounts = userDept === 'accounts' || userRole === 'accounts';

  // Filters & State
  const [searchParams] = useSearchParams();
  const [docTypeFilter, setDocTypeFilter] = useState<DocumentTypeFilter>('all');
  const [activeTab, setActiveTab] = useState<TaxInvoiceTab>(
    (searchParams.get('tab') as TaxInvoiceTab) || 'all'
  );

  useEffect(() => {
    const tabParam = searchParams.get('tab') as TaxInvoiceTab;
    if (tabParam) setActiveTab(tabParam);
  }, [searchParams]);
  const [searchQuery, setSearchQuery] = useState('');
  const [preparedByFilter, setPreparedByFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDocData, setSelectedDocData] = useState<DocumentWorkspaceData | null>(null);

  // Generate Tax Invoice Modal state
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selectedScheduleForGen, setSelectedScheduleForGen] = useState<any | null>(null);
  const [selectedMilestoneForGen, setSelectedMilestoneForGen] = useState<any | null>(null);
  const [generating, setGenerating] = useState(false);

  // Reject / Revise Modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectInvoiceTarget, setRejectInvoiceTarget] = useState<UnifiedTaxInvoice | null>(null);
  const [rejectReason, setRejectReason] = useState('Incorrect Tax / Amount Details');
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Record Payment Modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<UnifiedTaxInvoice | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Data queries
  const { data: invoiceRequestsData, isLoading: isLoadingRequests, refetch: refetchRequests } =
    useInvoiceRequests();
  const actions = useInvoiceRequestActions();

  const { data: schedulesData, isLoading: isLoadingSchedules, refetch: refetchSchedules } = useQuery({
    queryKey: ['tax-invoices-payment-schedules'],
    queryFn: async () => {
      try {
        const res = await api.get('/api/accounting/payment-schedules?limit=100');
        return res.data?.schedules || res.data?.data || res.data || [];
      } catch {
        return [];
      }
    },
    refetchInterval: 15000,
  });

  const isRefreshing = isLoadingRequests || isLoadingSchedules;

  // Build unified tax invoices list
  const taxInvoices: UnifiedTaxInvoice[] = useMemo(() => {
    const list: UnifiedTaxInvoice[] = [];
    const seenIds = new Set<string>();
    const seenInvoiceNumbers = new Set<string>();

    const rawRequests = invoiceRequestsData?.requests || [];

    // A tax invoice is written by whoever pressed Generate, so its own name is not "Prepared By".
    // Walk the row's chain (invoice -> proforma -> quotation) to the salesperson on the quotation.
    const docById = new Map<string, any>();
    for (const d of rawRequests) docById.set(String((d as any).id || (d as any)._id), d);
    const chainQuotationOf = (d: any): any => {
      const direct = docById.get(String(d?.sourceQuotationId));
      if (direct) return direct;
      const proforma = docById.get(String(d?.sourceProformaId || d?.parentProformaId));
      return proforma ? docById.get(String(proforma.sourceQuotationId)) : undefined;
    };

    // 1. Direct Invoice Requests (where requestType === 'invoice' OR number starts with TI/INV OR status is invoice-related)
    for (const r of rawRequests) {
      const isInvoiceDoc =
        r.requestType === 'invoice' ||
        (Boolean(r.number) && /^TI/i.test(r.number!)) ||
        (Boolean(r.invoiceNumber) && !r.invoiceNumber!.startsWith('PI') && !r.invoiceNumber!.startsWith('QT')) ||
        r.status === 'invoice_pending_approval' ||
        r.status === 'sent' ||
        (r as any).isInvoiceGenerated;

      if (!isInvoiceDoc) continue;

      const invNo =
        (r.invoiceNumber && !r.invoiceNumber.startsWith('PI') && !r.invoiceNumber.startsWith('QT')
          ? r.invoiceNumber
          : r.number) || `TI-${r.id.slice(-6).toUpperCase()}`;

      seenIds.add(r.id);
      seenInvoiceNumbers.add(invNo.toLowerCase());

      list.push({
        id: r.id,
        invoiceNumber: invNo,
        date: (r as any).createdAt || r.date,
        dueDate: (r as any).dueDate || (r as any).validUntil,
        clientName: r.clientName || 'Client',
        clientEmail: r.clientEmail,
        clientPhone: r.clientPhone,
        clientAddress: r.clientAddress,
        clientGstin: r.clientGstin,
        sourceQuotationNumber: r.sourceQuotationNumber,
        sourceQuotationId: r.sourceQuotationId,
        sourceProformaNumber: r.proformaNumber || (r as any).sourceProformaNumber,
        sourceProformaId: (r as any).sourceProformaId || (r as any).parentProformaId,
        milestoneIndex: r.milestoneIndex,
        milestonePhaseLabel: r.milestonePhaseLabel,
        subtotal: r.subtotal || 0,
        taxTotal: r.taxTotal || 0,
        total: r.total || 0,
        status: r.status,
        rejectionReason: (r as any).rejectionReason,
        revisionNote: (r as any).revisionNote || (r as any).reviewNote,
        rawRequest: r,
        items: r.items,
        notes: r.notes,
        preparedBy: preparedByOf(r, chainQuotationOf(r))
          || (r as any).requestedByName
          || (r as any).createdByName
          || (typeof (r as any).createdBy === 'string' ? '' : ((r as any).createdBy?.displayName || (r as any).createdBy?.name))
          || '',
      });
    }

    // 2. Extract tax invoices generated from Payment Schedules & Milestones
    const schedules = Array.isArray(schedulesData) ? schedulesData : [];
    for (const sch of schedules) {
      const milestones = Array.isArray(sch.milestones) ? sch.milestones : [];
      for (const [idx, ms] of milestones.entries()) {
        const hasInvoice =
          Boolean(ms.invoiceNumber) ||
          Boolean(ms.invoiceRequestId) ||
          ['invoice_generated', 'invoice_pending_approval', 'approved', 'revision_needed', 'invoice_rejected', 'paid', 'partially_paid'].includes(ms.status) ||
          ['invoice_generated', 'invoice_pending_approval', 'approved', 'revision_needed', 'invoice_rejected', 'paid', 'partially_paid'].includes(ms.invoiceStatus);

        if (!hasInvoice) continue;

        const msInvNo =
          ms.invoiceNumber ||
          (ms.invoiceRequestId && rawRequests.find((x: any) => String(x.id || x._id) === String(ms.invoiceRequestId))?.number) ||
          `TI-${sch.quotationNumber?.replace(/^QT\/?/i, '') || sch._id?.slice(-4)}-M${idx + 1}`;

        if (seenInvoiceNumbers.has(msInvNo.toLowerCase())) {
          // If already in list from invoice requests, enrich it
          const existing = list.find((x) => x.invoiceNumber.toLowerCase() === msInvNo.toLowerCase());
          if (existing) {
            existing.scheduleId = sch._id;
            existing.milestoneId = ms._id;
            existing.milestoneName = ms.name || existing.milestoneName;
            existing.rejectionReason = ms.rejectionReason || existing.rejectionReason;
            existing.revisionNote = ms.revisionNote || existing.revisionNote;
            if (['approved', 'revision_needed', 'invoice_rejected', 'paid', 'partially_paid', 'invoice_pending_approval'].includes(ms.status)) {
              existing.status = ms.status;
            }
          }
          continue;
        }

        seenInvoiceNumbers.add(msInvNo.toLowerCase());

        list.push({
          id: ms.invoiceRequestId || `${sch._id}-${ms._id}`,
          invoiceNumber: msInvNo,
          date: ms.dueDate || sch.createdAt,
          dueDate: ms.dueDate,
          clientName: sch.clientName || 'Client',
          clientEmail: sch.clientEmail,
          clientPhone: sch.clientPhone,
          clientAddress: sch.clientAddress,
          clientGstin: sch.clientGstin,
          sourceQuotationNumber: sch.quotationNumber,
          sourceQuotationId: sch.quotationId,
          sourceProformaNumber: sch.proformaNumber || ms.proformaNumber,
          sourceProformaId: sch.proformaRequestId || ms.proformaRequestId,
          milestoneIndex: idx,
          milestonePhaseLabel: ms.name,
          milestoneName: ms.name,
          subtotal: ms.milestoneTaxableAmount || (ms.percentage ? (sch.totalTaxable * ms.percentage) / 100 : 0),
          taxTotal: ms.milestoneGST || (ms.percentage ? (sch.totalGst * ms.percentage) / 100 : 0),
          total: ms.milestoneTotal || (ms.percentage ? (sch.totalAmount * ms.percentage) / 100 : 0),
          status: ms.status || 'sent',
          rejectionReason: ms.rejectionReason,
          revisionNote: ms.revisionNote,
          scheduleId: sch._id,
          milestoneId: ms._id,
          preparedBy: preparedByOf(sch as any, sch.quotationId as any)
            || (sch as any).createdByName
            || (typeof (sch as any).createdBy === 'string' ? '' : ((sch as any).createdBy?.displayName || (sch as any).createdBy?.name))
            || '',
        });
      }
    }

    return list.sort((a, b) => (b.invoiceNumber || '').localeCompare(a.invoiceNumber || ''));
  }, [invoiceRequestsData, schedulesData]);

  // Filtered Tax Invoices
  const filteredInvoices = useMemo(() => {
    return taxInvoices.filter((inv) => {
      // Document type filter (all, tax_invoice, running_bill)
      const isRb = isInvoiceRunningBill(inv);
      if (docTypeFilter === 'running_bill' && !isRb) return false;
      if (docTypeFilter === 'tax_invoice' && isRb) return false;

      // Prepared-by filter
      if (preparedByFilter && (inv.preparedBy || '') !== preparedByFilter) return false;

      // Status filter
      if (activeTab === 'pending_approval' && inv.status !== 'invoice_pending_approval' && inv.status !== 'invoice_generated' && inv.status !== 'requested') return false;
      if (activeTab === 'approved' && inv.status !== 'approved' && inv.status !== 'sent') return false;
      if (activeTab === 'revision_needed' && inv.status !== 'revision_needed' && inv.status !== 'invoice_rejected') return false;
      if (activeTab === 'paid' && inv.status !== 'paid' && inv.status !== 'confirmed') return false;
      if (activeTab === 'draft' && inv.status !== 'draft') return false;

      // Text search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesInvNo = inv.invoiceNumber?.toLowerCase().includes(q);
        const matchesClient = inv.clientName?.toLowerCase().includes(q);
        const matchesGstin = inv.clientGstin?.toLowerCase().includes(q);
        const matchesQt = inv.sourceQuotationNumber?.toLowerCase().includes(q);
        const matchesPi = inv.sourceProformaNumber?.toLowerCase().includes(q);
        if (!matchesInvNo && !matchesClient && !matchesGstin && !matchesQt && !matchesPi) {
          return false;
        }
      }

      // Date range filter
      if (dateFrom || dateTo) {
        const invDate = inv.date ? new Date(inv.date) : null;
        if (invDate) {
          const from = dateFrom ? new Date(dateFrom) : null;
          const to = dateTo ? new Date(dateTo) : null;
          if (from && invDate < from) return false;
          if (to) {
            const endOfDay = new Date(to);
            endOfDay.setHours(23, 59, 59, 999);
            if (invDate > endOfDay) return false;
          }
        }
      }

      return true;
    });
  }, [taxInvoices, docTypeFilter, activeTab, searchQuery, dateFrom, dateTo, preparedByFilter]);

  // Distinct sales reps who prepared at least one listed invoice.
  const preparedByOptions = useMemo(
    () => Array.from(new Set(taxInvoices.map((inv) => inv.preparedBy).filter(Boolean))).sort() as string[],
    [taxInvoices]
  );

  // KPI Metrics
  const stats = useMemo(() => {
    let totalInvoiced = 0;
    let pendingCount = 0;
    let pendingAmount = 0;
    let approvedCount = 0;
    let approvedAmount = 0;
    let revisionCount = 0;
    let paidCount = 0;
    let paidAmount = 0;

    for (const inv of taxInvoices) {
      totalInvoiced += inv.total || 0;
      if (inv.status === 'invoice_pending_approval' || inv.status === 'invoice_generated' || inv.status === 'requested') {
        pendingCount += 1;
        pendingAmount += inv.total || 0;
      }
      if (inv.status === 'approved' || inv.status === 'sent') {
        approvedCount += 1;
        approvedAmount += inv.total || 0;
      }
      if (inv.status === 'revision_needed' || inv.status === 'invoice_rejected') {
        revisionCount += 1;
      }
      if (inv.status === 'paid' || inv.status === 'confirmed') {
        paidCount += 1;
        paidAmount += inv.total || 0;
      }
    }

    return {
      totalInvoiced,
      totalCount: taxInvoices.length,
      pendingCount,
      pendingAmount,
      approvedCount,
      approvedAmount,
      revisionCount,
      paidCount,
      paidAmount,
    };
  }, [taxInvoices]);

  const runningBillCount = useMemo(() => {
    return taxInvoices.filter(isInvoiceRunningBill).length;
  }, [taxInvoices]);

  const taxInvoiceCount = useMemo(() => {
    return taxInvoices.length - runningBillCount;
  }, [taxInvoices, runningBillCount]);

  const draftCount = useMemo(() => {
    return taxInvoices.filter((x) => x.status === 'draft').length;
  }, [taxInvoices]);

  // Find milestones and proformas eligible for tax invoice generation
  const readyForInvoiceMilestones = useMemo(() => {
    const list: Array<{ schedule: any; milestone: any; index: number }> = [];
    const schedules = Array.isArray(schedulesData) ? schedulesData : [];
    for (const sch of schedules) {
      const milestones = Array.isArray(sch.milestones) ? sch.milestones : [];
      for (const [idx, ms] of milestones.entries()) {
        const isEligible =
          !ms.invoiceNumber &&
          ms.status !== 'invoice_generated' &&
          ms.status !== 'paid' &&
          (ms.status === 'proforma_approved' || ms.status === 'approved' || Boolean(ms.proformaNumber) || Boolean(sch.proformaNumber));
        if (isEligible) {
          list.push({ schedule: sch, milestone: ms, index: idx });
        }
      }
    }
    return list;
  }, [schedulesData]);

  // Generate & Open HTML in Workspace Drawer
  const handleOpenPreview = (inv: UnifiedTaxInvoice) => {
    const coLogo = companyProfile?.logo || '';
    const coSig = (inv.rawRequest as any)?.template_data?.signatureUrl || '';
    const coBankAccName = (inv.rawRequest as any)?.bankAccName || (inv.rawRequest as any)?.template_data?.bankAccName || defaultBankDetail?.accountName || '';
    const coBankAccNo = (inv.rawRequest as any)?.bankAccNo || (inv.rawRequest as any)?.template_data?.bankAccNo || defaultBankDetail?.accountNumber || '';
    const coBankName = (inv.rawRequest as any)?.bankName || (inv.rawRequest as any)?.template_data?.bankName || defaultBankDetail?.bankName || '';
    // The saved document does not store the company's state; it is read from the live company profile.
    const coState: string = (companyProfile as any)?.registeredAddress?.state || '';
    const coBankBranch = (inv.rawRequest as any)?.bankBranch || (inv.rawRequest as any)?.template_data?.bankBranch || defaultBankDetail?.branch || '';
    const coBankIfsc = (inv.rawRequest as any)?.bankIfsc || (inv.rawRequest as any)?.template_data?.bankIfsc || defaultBankDetail?.ifsc || '';
    const coBankUpi = (inv.rawRequest as any)?.bankUpi || (inv.rawRequest as any)?.template_data?.bankUpi || defaultBankDetail?.upi || '';
    // Company identity for the fallback generator: the running-bill and final-invoice
    // paths take it from headerFields(), but this direct path only received the
    // logo/state/bank — so the header printed with no name, GSTIN, PAN or contact.
    const coProfile = (companyProfile || {}) as any;
    const coAddrReg = coProfile?.registeredAddress;
    const coAddr = coAddrReg ? [coAddrReg.line1, coAddrReg.line2, coAddrReg.city, coAddrReg.state, coAddrReg.pincode].filter(Boolean).join(', ') : '';
    const coName = coProfile?.legalName || coProfile?.name || '';
    const coGstin = coProfile?.gstin || coProfile?.gst || '';
    const coPan = coProfile?.pan || '';
    const coPhone = coProfile?.phone || '';
    const coEmail = coProfile?.officialEmail || coProfile?.email || '';
    const coWeb = coProfile?.website || '';

    const schedulesList = Array.isArray(schedulesData) ? schedulesData : [];
    const linkedSchedule = schedulesList.find((s: any) =>
      (inv.scheduleId && String(s._id) === String(inv.scheduleId)) ||
      s.milestones?.some((m: any) => (m.invoiceNumber && m.invoiceNumber.toLowerCase() === inv.invoiceNumber.toLowerCase()) || (m.invoiceRequestId && String(m.invoiceRequestId) === String(inv.id))) ||
      (inv.sourceQuotationNumber && s.quotationNumber === inv.sourceQuotationNumber) ||
      (inv.sourceProformaNumber && s.proformaNumber === inv.sourceProformaNumber)
    );

    const msIdx = linkedSchedule?.milestones
      ? linkedSchedule.milestones.findIndex((m: any) =>
          (m.invoiceNumber && m.invoiceNumber.toLowerCase() === inv.invoiceNumber.toLowerCase()) ||
          (m.invoiceRequestId && String(m.invoiceRequestId) === String(inv.id)) ||
          (inv.milestoneId && String(m._id) === String(inv.milestoneId)) ||
          (inv.milestoneName && m.name === inv.milestoneName)
        )
      : (inv.milestoneIndex ?? 0);

    const activeMs = (linkedSchedule?.milestones && msIdx >= 0) ? linkedSchedule.milestones[msIdx] : null;
    const stageName = activeMs?.name || inv.milestoneName || inv.milestonePhaseLabel || 'Stage 1: Advance / Mobilization';
    const stagePct = activeMs?.percentage || (inv.rawRequest as any)?.stagePercent || (linkedSchedule?.milestones?.length ? (100 / linkedSchedule.milestones.length) : 100);

    const contractTaxable = Number(linkedSchedule?.totalTaxable || (linkedSchedule?.totalAmount && linkedSchedule?.totalGst ? linkedSchedule.totalAmount - linkedSchedule.totalGst : 0) || (inv.subtotal && stagePct > 0 ? (inv.subtotal / (stagePct / 100)) : inv.subtotal));
    const contractGrand = Number(linkedSchedule?.totalAmount || (inv.total && stagePct > 0 ? (inv.total / (stagePct / 100)) : inv.total));
    const contractTax = Number(linkedSchedule?.totalGst || (inv.taxTotal && stagePct > 0 ? (inv.taxTotal / (stagePct / 100)) : inv.taxTotal));

    const scheduleMilestones = linkedSchedule?.milestones?.length
      ? linkedSchedule.milestones.map((m: any) => ({
          name: m.name || stageName,
          percent: m.percentage,
          due: m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : `Phase ${m.milestoneNumber}`,
          invoiceNumber: m.invoiceNumber,
          proformaNumber: m.proformaNumber,
          netAmount: m.milestoneTaxableAmount,
          taxAmount: m.milestoneGST,
          amountDue: m.milestoneTotal,
        }))
      : (inv.rawRequest?.milestones?.length
        ? inv.rawRequest.milestones.map((m: any) => ({
            name: m.name || stageName,
            percent: m.percentage || m.percent,
            due: m.dueDate ? new Date(m.dueDate).toLocaleDateString('en-IN') : 'Due on receipt',
            invoiceNumber: m.invoiceNumber,
            proformaNumber: m.proformaNumber,
          }))
        : [{
            name: stageName,
            percent: stagePct,
            due: inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN') : 'Due on receipt',
            invoiceNumber: inv.invoiceNumber,
            netAmount: inv.subtotal,
            taxAmount: inv.taxTotal,
            amountDue: inv.total,
          }]);

    const showDrawer = (html: string) => {
      const drawerDoc: DocumentWorkspaceData = {
        id: inv.id,
        number: inv.invoiceNumber,
        documentNumber: inv.invoiceNumber,
        clientName: inv.clientName,
        status: inv.status,
        requestType: 'invoice',
        htmlBody: html,
        currentHtmlBody: html,
        subtotal: inv.subtotal,
        taxTotal: inv.taxTotal,
        total: inv.total,
        grandTotal: inv.total,
        customerAddress: inv.clientAddress,
        customerEmail: inv.clientEmail,
        customerPhone: inv.clientPhone,
        canApproveNow: (isSuperAdmin || isAccounts) && (inv.status === 'invoice_pending_approval' || inv.status === 'requested'),
        canRejectNow: (isSuperAdmin || isAccounts) && (inv.status === 'invoice_pending_approval' || inv.status === 'requested'),
        canResubmit: (inv.status === 'revision_needed' || inv.status === 'invoice_rejected'),
        canRequestTaxInvoiceApproval: !(isSuperAdmin || isAccounts) ? false : (inv.status === 'approved' || inv.status === 'invoice_generated' || inv.status === 'sent'),
        userRole: currentUser?.role,
      };

      setSelectedDocData(drawerDoc);
      setDrawerOpen(true);
    };

    // The final tax invoice: the whole contract plus the running bills it adjusts.
    if ((inv.rawRequest as any)?.isFinalInvoice) {
      showDrawer(generateFullTaxInvoiceHtml(applyFinalInvoice({
        companyLogo: coLogo,
        companyState: coState,
        signatureUrl: coSig,
        bankAccName: coBankAccName,
        bankAccNo: coBankAccNo,
        bankName: coBankName,
        bankBranch: coBankBranch,
        bankIfsc: coBankIfsc,
        bankUpi: coBankUpi,
      } as any, inv.rawRequest as any, {
        company: companyProfile as any,
        defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
      })));
      return;
    }

    // A schedule billed stage by stage shows its running bill: the stage's own figures from the server.
    if (linkedSchedule && activeMs && linkedSchedule.paymentType !== 'full_payment' && (linkedSchedule.milestones?.length ?? 0) > 1) {
      api.get(`/api/accounting/payment-schedules/${linkedSchedule._id}/milestones/${activeMs._id}/running-bill`, { skipErrorToast: true } as any)
        .then((res: any) => {
          const rb = res.data?.data;
          if (!rb) throw new Error('empty');
          showDrawer(generateFullTaxInvoiceHtml(applyRunningBill({
            date: inv.date ? new Date(inv.date).toLocaleDateString('en-IN') : undefined,
            customerName: inv.clientName,
            customerEmail: inv.clientEmail,
            customerContact: inv.clientPhone,
            customerAddress: inv.clientAddress,
            customerGstin: inv.clientGstin,
            signatureUrl: coSig,
            companyLogo: coLogo,
            companyState: coState,
            bankAccName: coBankAccName,
            bankAccNo: coBankAccNo,
            bankName: coBankName,
            bankBranch: coBankBranch,
            bankIfsc: coBankIfsc,
            bankUpi: coBankUpi,
          } as any, rb, {
            quotationNumber: linkedSchedule.quotationNumber,
            proformaNumber: String(linkedSchedule.proformaNumber || activeMs.proformaNumber || inv.sourceProformaNumber || '').replace(/\s*\([IVXLCDM\d]+\)\s*$/i, '').trim(),
            company: companyProfile as any,
            defaultDeclaration: (masterData?.declarations ?? []).find((d: any) => d.isDefault && d.isActive !== false)?.content,
          })));
        })
        .catch((err: any) => toast.error(getApiErrorMessage(err, 'load the running bill')));
      return;
    }

    let html = (inv.rawRequest as any)?.currentHtmlBody || (inv.rawRequest as any)?.invoiceHtmlBody || '';

    if (!html || !html.includes('Tax Invoice') || /full\s*(?:time\s*)?payment/i.test(html)) {
      html = generateFullTaxInvoiceHtml({
        documentNumber: inv.invoiceNumber,
        invoiceNumber: inv.invoiceNumber,
        date: inv.date ? new Date(inv.date).toLocaleDateString('en-IN') : undefined,
        dueDate: inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('en-IN') : undefined,
        customerName: inv.clientName,
        customerEmail: inv.clientEmail,
        customerContact: inv.clientPhone,
        customerAddress: inv.clientAddress,
        customerGstin: inv.clientGstin,
        items: inv.items && inv.items.length > 0 ? inv.items : [
          {
            name: stageName,
            description: `Milestone: ${stageName} (${stagePct}% milestone share)`,
            quantity: 1,
            unit: 'Nos',
            rate: inv.subtotal,
            taxRate: 18,
            total: inv.subtotal,
          }
        ],
        notes: inv.notes || '',
        subtotal: inv.subtotal,
        taxTotal: inv.taxTotal,
        total: inv.total,
        contractTaxable: contractTaxable > 0 ? contractTaxable : undefined,
        contractGrand: contractGrand > 0 ? contractGrand : undefined,
        contractTax: contractTax > 0 ? contractTax : undefined,
        stagePercent: stagePct,
        milestoneIndex: msIdx >= 0 ? msIdx : 0,
        milestoneName: stageName,
        milestonePhaseLabel: stageName,
        stageAmountsAreFinal: true,
        paymentType: 'milestone_based',
        milestones: scheduleMilestones,
        signatureUrl: coSig,
        companyLogo: coLogo,
        companyState: coState,
        companyName: coName,
        companyAddress: coAddr,
        companyGstin: coGstin,
        companyPan: coPan,
        companyPhone: coPhone,
        companyEmail: coEmail,
        companyWeb: coWeb,
        bankAccName: coBankAccName,
        bankAccNo: coBankAccNo,
        bankName: coBankName,
        bankBranch: coBankBranch,
        bankIfsc: coBankIfsc,
        bankUpi: coBankUpi,
      });
    } else if (html && /full\s*(?:time\s*)?payment/i.test(html)) {
      html = html.replace(/Full Payment \(\s*100%\s*\)/gi, stageName)
                 .replace(/Full Payment/gi, stageName);
    }

    showDrawer(html);
  };

  // Direct Tax Invoice Approval
  const handleApprove = async (inv: UnifiedTaxInvoice) => {
    try {
      if (inv.scheduleId && inv.milestoneId) {
        if (inv.status === 'invoice_pending_approval') {
          await api.post(`/payment-schedules/${inv.scheduleId}/milestones/${inv.milestoneId}/approve-invoice`);
        } else {
          await api.post(`/payment-schedules/${inv.scheduleId}/milestones/${inv.milestoneId}/approve-milestone`);
        }
      } else if (inv.rawRequest) {
        await actions.approveInvoiceGeneration.mutateAsync({ id: inv.rawRequest.id });
      }
      toast.success(`Tax invoice ${inv.invoiceNumber} approved successfully`);
      refetchRequests();
      refetchSchedules();
      queryClient.invalidateQueries({ queryKey: ['tax-invoices-payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to approve tax invoice');
    }
  };

  // Open Reject / Revise Modal
  const handleOpenReject = (inv: UnifiedTaxInvoice) => {
    setRejectInvoiceTarget(inv);
    setRejectReason('Incorrect Tax / Amount Details');
    setRejectNote('');
    setRejectModalOpen(true);
  };

  // Submit Reject / Revise
  const handleSubmitReject = async () => {
    if (!rejectInvoiceTarget) return;
    if (!rejectNote.trim()) {
      toast.error('Please enter a note describing the required revisions.');
      return;
    }
    setRejecting(true);
    try {
      if (rejectInvoiceTarget.scheduleId && rejectInvoiceTarget.milestoneId) {
        await api.post(
          `/payment-schedules/${rejectInvoiceTarget.scheduleId}/milestones/${rejectInvoiceTarget.milestoneId}/reject-invoice`,
          {
            reason: rejectReason,
            note: rejectNote.trim(),
          }
        );
      } else if (rejectInvoiceTarget.rawRequest) {
        await actions.reject.mutateAsync({
          id: rejectInvoiceTarget.rawRequest.id,
          stage: 'quotation',
          reason: `${rejectReason}: ${rejectNote.trim()}`,
        });
      }
      toast.success(`Tax invoice ${rejectInvoiceTarget.invoiceNumber} returned for revision`);
      setRejectModalOpen(false);
      setRejectInvoiceTarget(null);
      refetchRequests();
      refetchSchedules();
      queryClient.invalidateQueries({ queryKey: ['tax-invoices-payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['payment-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-requests'] });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to request revision');
    } finally {
      setRejecting(false);
    }
  };

  // Record Payment
  const handleRecordPaymentSubmit = async () => {
    if (!paymentInvoice || !paymentInvoice.scheduleId || !paymentInvoice.milestoneId) {
      toast.error('Cannot record payment for this invoice without an active payment schedule');
      return;
    }
    setRecordingPayment(true);
    try {
      await api.post(
        `/api/accounting/payment-schedules/${paymentInvoice.scheduleId}/milestones/${paymentInvoice.milestoneId}/record-payment`,
        {
          // No amount means "everything still outstanding" (the server caps it there).
          amount: paymentAmount > 0 ? paymentAmount : undefined,
          paymentMode: paymentMethod,
          referenceNo: paymentRef || `PAY-${Date.now().toString().slice(-6)}`,
          note: `Payment recorded for ${paymentInvoice.invoiceNumber}`,
        }
      );
      toast.success(`Payment recorded for ${paymentInvoice.invoiceNumber}`);
      setPaymentModalOpen(false);
      refetchSchedules();
      refetchRequests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  // Execute tax invoice generation
  const handleGenerateInvoice = async () => {
    if (!selectedScheduleForGen || !selectedMilestoneForGen) {
      toast.error('Please select an eligible milestone to generate Tax Invoice');
      return;
    }
    setGenerating(true);
    try {
      await api.post(
        `/api/accounting/payment-schedules/${selectedScheduleForGen._id}/milestones/${selectedMilestoneForGen._id}/generate-invoice`
      );
      toast.success('Tax invoice generated successfully!');
      setGenerateModalOpen(false);
      setSelectedScheduleForGen(null);
      setSelectedMilestoneForGen(null);
      refetchSchedules();
      refetchRequests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to generate tax invoice');
    } finally {
      setGenerating(false);
    }
  };

  // Status badge renderer matching HR patterns
  const statusBadge = (s: string) => {
    const statusConfig: Record<string, { bg: string; text: string; border: string; icon: React.ReactNode; label: string }> = {
      paid: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', icon: <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />, label: 'Paid' },
      confirmed: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', icon: <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />, label: 'Confirmed' },
      approved: { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-300', icon: <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />, label: 'Approved' },
      sent: { bg: 'bg-blue-50', text: 'text-blue-800', border: 'border-blue-300', icon: <Check className="w-3 h-3 mr-1 text-blue-600" />, label: 'Issued & Sent' },
      invoice_pending_approval: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300', icon: <Clock className="w-3 h-3 mr-1 text-amber-600" />, label: 'Awaiting Approval' },
      invoice_generated: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300', icon: <Clock className="w-3 h-3 mr-1 text-amber-600" />, label: 'Awaiting Approval' },
      requested: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-300', icon: <Clock className="w-3 h-3 mr-1 text-amber-600" />, label: 'Awaiting Approval' },
      revision_needed: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300', icon: <AlertCircle className="w-3 h-3 mr-1 text-orange-600" />, label: 'Revision Needed' },
      invoice_rejected: { bg: 'bg-orange-50', text: 'text-orange-800', border: 'border-orange-300', icon: <AlertCircle className="w-3 h-3 mr-1 text-orange-600" />, label: 'Revision Needed' },
      draft: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', icon: <FileText className="w-3 h-3 mr-1 text-slate-500" />, label: 'Draft' },
    };

    const cfg = statusConfig[s] || { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', icon: <FileText className="w-3 h-3 mr-1 text-slate-500" />, label: s };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text} ${cfg.border} shadow-xs`}>
        {cfg.icon}
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pr-1">
      <AccountingSubNav />

      {/* Header - matching HR pattern with gradient icon circle */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 hover:shadow-md transition-shadow"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200">
            <Receipt className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block text-[11px] font-bold tracking-wider text-[#6825F5] uppercase">
              TAX INVOICES
            </span>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[#11183F]">
              Manage, approve, issue, and track official GST tax invoices.
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              refetchRequests();
              refetchSchedules();
            }}
            disabled={isRefreshing}
            className="font-medium"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={() => setGenerateModalOpen(true)}
            className="bg-[#6825F5] hover:bg-[#581cd6] text-white font-medium shadow-sm shadow-purple-200"
          >
            <PlusCircle className="h-4 w-4 mr-1.5" />
            Generate Tax Invoice
          </Button>
        </div>
      </div>

      {/* KPI Stats - using StatTile from HR components */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          icon={Receipt}
          label="Total Invoiced"
          value={formatINR(stats.totalInvoiced)}
          tone="primary"
          sublabel={`Across ${stats.totalCount} tax invoices`}
          index={0}
        />
        <StatTile
          icon={Clock}
          label="Awaiting Approval"
          value={formatINR(stats.pendingAmount)}
          tone="warning"
          sublabel={`${stats.pendingCount} invoices pending review`}
          index={1}
        />
        <StatTile
          icon={CheckCircle2}
          label="Collected / Paid"
          value={formatINR(stats.paidAmount)}
          tone="success"
          sublabel={`${stats.paidCount} fully paid & reconciled`}
          index={2}
        />
        <StatTile
          icon={AlertCircle}
          label="Outstanding / Due"
          value={formatINR(Math.max(0, stats.totalInvoiced - stats.paidAmount))}
          tone="danger"
          sublabel="Awaiting customer remittance"
          index={3}
        />
      </div>

      {/* Main Table Card - HR pattern */}
      <div className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Receipt className="h-6 w-6 text-[#6825F5]" />
            <h2 className="text-lg font-bold tracking-tight text-[#11183F]">
              All Tax Invoices ({filteredInvoices.length})
            </h2>
          </div>

          {/* Filter Toolbar: Search Bar -> Type Dropdown -> Status Dropdown -> Date Range Picker */}
          <div className="flex flex-wrap items-center gap-2.5 ml-auto">
            {/* Search Box */}
            <div className="relative min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#747C9C]" />
              <input
                type="text"
                placeholder="Search by invoice #, client, GSTIN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] placeholder:text-[#747C9C] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
              />
            </div>

            {/* 1. Document Type Dropdown */}
            <div className="relative min-w-[155px]">
              <select
                value={docTypeFilter}
                onChange={(e) => setDocTypeFilter(e.target.value as DocumentTypeFilter)}
                className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent cursor-pointer appearance-none shadow-xs"
              >
                <option value="all">All Documents ({taxInvoices.length})</option>
                <option value="tax_invoice">Tax Invoices ({taxInvoiceCount})</option>
                <option value="running_bill">Running Bills ({runningBillCount})</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[#747C9C]">
                <ChevronDown className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* 2. Status Dropdown */}
            <div className="relative min-w-[170px]">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as TaxInvoiceTab)}
                className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent cursor-pointer appearance-none shadow-xs"
              >
                <option value="all">All Statuses ({taxInvoices.length})</option>
                <option value="pending_approval">Pending Approval ({stats.pendingCount})</option>
                <option value="approved">Approved ({stats.approvedCount})</option>
                <option value="revision_needed">Revision Needed ({stats.revisionCount})</option>
                <option value="paid">Paid ({stats.paidCount})</option>
                <option value="draft">Drafts ({draftCount})</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[#747C9C]">
                <ChevronDown className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* 3. Prepared-by Dropdown */}
            <div className="relative min-w-[170px]">
              <select
                value={preparedByFilter}
                onChange={(e) => setPreparedByFilter(e.target.value)}
                aria-label="Filter by prepared by"
                className="w-full pl-3 pr-8 py-1.5 text-xs font-medium bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent cursor-pointer appearance-none shadow-xs"
              >
                <option value="">All Prepared By</option>
                {preparedByOptions.map((name) => (
                  <option key={name} value={name}>Prepared by {name}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-[#747C9C]">
                <ChevronDown className="h-3.5 w-3.5" />
              </div>
            </div>

            {/* 4. Date Range Filter */}
            <div className="flex items-center gap-1.5 bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg px-2.5 py-1.5 shadow-xs">
              <Calendar className="h-3.5 w-3.5 text-[#747C9C] shrink-0" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-xs bg-transparent border-none text-[#11183F] placeholder:text-[#747C9C] focus:outline-none focus:ring-0 w-[120px]"
                placeholder="From date"
              />
              <span className="text-[#747C9C] text-xs">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-xs bg-transparent border-none text-[#11183F] placeholder:text-[#747C9C] focus:outline-none focus:ring-0 w-[120px]"
                placeholder="To date"
              />
              {(dateFrom || dateTo) && (
                <button
                  type="button"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                  className="text-[#747C9C] hover:text-[#DC2626] cursor-pointer ml-1"
                  title="Clear date filter"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Clear Filters Reset Button */}
            {(docTypeFilter !== 'all' || activeTab !== 'all' || searchQuery || dateFrom || dateTo || preparedByFilter) && (
              <button
                type="button"
                onClick={() => {
                  setDocTypeFilter('all');
                  setActiveTab('all');
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                  setPreparedByFilter('');
                }}
                className="text-xs font-semibold text-[#6825F5] hover:text-[#581cd6] px-2.5 py-1.5 rounded-lg border border-dashed border-[#A5B4FC] hover:bg-[#EDE9FE] transition-colors"
                title="Reset all filters"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border border-[#F1F3F9]">
          {isRefreshing && taxInvoices.length === 0 ? (
            <div className="flex items-center justify-center p-12">
              <Spinner className="h-6 w-6 text-[#6825F5]" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8">
              <EmptyState
                title={searchQuery ? 'No matching tax invoices' : 'No tax invoices found'}
                description={
                  searchQuery || docTypeFilter !== 'all' || activeTab !== 'all' || dateFrom || dateTo
                    ? 'Try clearing the search query or changing your filters.'
                    : 'Generate a tax invoice from an approved proforma or milestone to see it here.'
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-[#F8F9FE] text-[#747C9C] font-bold text-[11px] tracking-wider uppercase border-b border-[#F1F3F9]">
                  <tr>
                    <th className="px-5 py-3.5">Invoice #</th>
                    <th className="px-4 py-3.5">Client</th>
                    <th className="px-4 py-3.5">Milestone / Stage</th>
                    <th className="px-4 py-3.5">Reference</th>
                    <th className="px-4 py-3.5 text-right">Taxable</th>
                    <th className="px-4 py-3.5 text-right">Total Amount</th>
                    <th className="px-4 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F3F9]">
                  {filteredInvoices.map((inv) => {
                    const isPending =
                      inv.status === 'invoice_pending_approval' ||
                      inv.status === 'invoice_generated' ||
                      inv.status === 'requested';
                    const isApproved = inv.status === 'approved';
                    const isRevisionNeeded =
                      inv.status === 'revision_needed' || inv.status === 'invoice_rejected';
                    const isPaid = inv.status === 'paid' || inv.status === 'confirmed';
                    const isSent = inv.status === 'sent';

                    return (
                      <tr key={inv.id} className="hover:bg-[#F8F9FE]/60 transition-colors">
                        {/* Invoice Number */}
                        <td className="px-5 py-3.5 font-medium">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold bg-[#EDE9FE] text-[#6825F5]">
                              <Receipt className="h-4 w-4" />
                            </div>
                            <div>
                              <span className="block text-sm font-semibold text-[#11183F] leading-tight">
                                {inv.invoiceNumber}
                              </span>
                              {(inv.rawRequest as any)?.isFinalInvoice && (
                                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                  Final Tax Invoice
                                </span>
                              )}
                              {isInvoiceRunningBill(inv) && (
                                <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                                  Running Bill{(inv.rawRequest as any)?.stageNumber ? ` · Stage ${(inv.rawRequest as any).stageNumber}/${(inv.rawRequest as any).stageCount || ''}` : ''}
                                </span>
                              )}
                              {inv.date && (
                                <span className="text-[10px] text-[#747C9C] block mt-0.5">
                                  {new Date(inv.date).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Client */}
                        <td className="px-4 py-3.5 text-sm text-[#475569]">
                          <div className="font-medium text-[#11183F]">{inv.clientName}</div>
                          {inv.preparedBy && (
                            <div className="text-[10px] text-[#747C9C]">Prepared by {inv.preparedBy}</div>
                          )}
                          {inv.clientGstin ? (
                            <span className="text-[10px] text-[#747C9C] font-mono">
                              GST: {inv.clientGstin}
                            </span>
                          ) : (
                            <span className="text-[10px] text-[#747C9C] italic">Unregistered</span>
                          )}
                        </td>

                        {/* Milestone / Stage */}
                        <td className="px-4 py-3.5 text-sm text-[#475569]">
                          {inv.milestonePhaseLabel || inv.milestoneName ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#EDE9FE] text-[#6825F5] border border-purple-200">
                              {inv.milestonePhaseLabel || inv.milestoneName}
                            </span>
                          ) : (
                            <span className="text-[#747C9C] text-[11px]">Full Contract</span>
                          )}
                        </td>

                        {/* Reference (Quot / Proforma) */}
                        <td className="px-4 py-3.5 text-sm text-[#475569]">
                          {inv.sourceProformaNumber && (
                            <div className="font-mono text-xs text-[#6825F5]">
                              {inv.sourceProformaNumber}
                            </div>
                          )}
                          {inv.sourceQuotationNumber && (
                            <div className="font-mono text-xs text-[#747C9C]">
                              {inv.sourceQuotationNumber}
                            </div>
                          )}
                          {!inv.sourceProformaNumber && !inv.sourceQuotationNumber && (
                            <span className="text-[#747C9C]">—</span>
                          )}
                        </td>

                        {/* Taxable Subtotal */}
                        <td className="px-4 py-3.5 text-right font-mono text-[#475569]">
                          {formatINR(inv.subtotal)}
                        </td>

                        {/* Total Amount */}
                        <td className="px-4 py-3.5 text-right font-mono font-semibold text-[#11183F]">
                          {formatINR(inv.total)}
                          {inv.taxTotal > 0 && (
                            <span className="text-[10px] text-[#747C9C] font-normal ml-1 block">
                              (incl. {formatINR(inv.taxTotal)} GST)
                            </span>
                          )}
                        </td>

                        {/* Status Badge */}
                        <td className="px-4 py-3.5">
                          {statusBadge(inv.status)}
                          {isRevisionNeeded && inv.rejectionReason && (
                            <span
                              className="text-[10px] text-orange-700 max-w-[150px] truncate block mt-1"
                              title={`${inv.rejectionReason}${inv.revisionNote ? `: ${inv.revisionNote}` : ''}`}
                            >
                              {inv.rejectionReason}
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* View / Preview */}
                            <button
                              type="button"
                              onClick={() => handleOpenPreview(inv)}
                              className="p-1.5 text-[#747C9C] hover:text-[#6825F5] hover:bg-[#EDE9FE] rounded-lg transition-colors cursor-pointer"
                              title="Preview Tax Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            {/* Request Approval (Accounts / Sales — for approved/sent invoices) */}
                            {isAccounts && (isApproved || isSent) && !isPaid && inv.rawRequest && (
                              <button
                                type="button"
                                onClick={async () => {
                                  await actions.requestInvoice.mutateAsync({ id: inv.rawRequest!.id, note: 'Requesting tax invoice approval' });
                                  toast.success('Tax invoice approval requested');
                                  refetchRequests();
                                  refetchSchedules();
                                }}
                                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-[#FFF7ED] px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-[#FFEDD5] transition-colors cursor-pointer"
                                title="Request Superadmin approval"
                              >
                                <Send className="h-3 w-3" /> Request Approval
                              </button>
                            )}

                            {/* Resubmit (for revision_needed / invoice_rejected) */}
                            {(isSuperAdmin || isAccounts) && (isRevisionNeeded) && inv.rawRequest && (
                              <button
                                type="button"
                                onClick={() => {
                                  setRejectInvoiceTarget(inv);
                                  setRejectReason(inv.rejectionReason || 'Incorrect Tax / Amount Details');
                                  setRejectNote(inv.revisionNote || '');
                                  setRejectModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-[#D1FAE5] px-3 py-1 text-xs font-semibold text-[#059669] hover:bg-[#A7F3D0] transition-colors cursor-pointer"
                                title="Resubmit for approval"
                              >
                                <Undo2 className="h-3 w-3" /> Resubmit
                              </button>
                            )}

                            {/* Approve (Superadmin / Accounts) */}
                            {isPending && (isSuperAdmin || isAccounts) && (
                              <button
                                type="button"
                                onClick={() => handleApprove(inv)}
                                className="inline-flex items-center gap-1 rounded-full bg-[#059669] px-3.5 py-1 text-xs font-semibold text-white hover:bg-[#047857] transition-colors cursor-pointer"
                                title="Approve Tax Invoice"
                              >
                                <Check className="h-3 w-3" /> Approve
                              </button>
                            )}

                            {/* Reject / Revise (Superadmin / Accounts) */}
                            {isPending && (isSuperAdmin || isAccounts) && (
                              <button
                                type="button"
                                onClick={() => handleOpenReject(inv)}
                                className="inline-flex items-center gap-1 rounded-full bg-[#DC2626] px-3 py-1 text-xs font-semibold text-white hover:bg-[#B91C1C] transition-colors cursor-pointer"
                                title="Reject / Request Revision"
                              >
                                <XCircle className="h-3.5 w-3.5" /> Reject / Revise
                              </button>
                            )}

                            {/* Record Payment (if issued/approved & not yet fully paid) */}
                            {(isSent || isApproved) && !isPaid && inv.scheduleId && (
                              <button
                                type="button"
                                onClick={() => {
                                  setPaymentInvoice(inv);
                                  setPaymentAmount(inv.total);
                                  setPaymentModalOpen(true);
                                }}
                                className="inline-flex items-center gap-1 rounded-full bg-[#10B981] px-3.5 py-1 text-xs font-semibold text-white hover:bg-[#059669] transition-colors cursor-pointer"
                                title="Record payment"
                              >
                                <CreditCard className="h-3 w-3" /> Paid
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Generate Tax Invoice Modal */}
      <Modal
        open={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        title="Generate Tax Invoice"
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-xs text-[#747C9C]">
            Select an approved proforma milestone or contract milestone to generate an official GST Tax Invoice.
          </p>

          {readyForInvoiceMilestones.length === 0 ? (
            <div className="p-6 text-center bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg">
              <p className="text-xs text-[#747C9C]">
                No milestones are currently awaiting tax invoice generation.
              </p>
              <p className="text-[11px] text-[#747C9C] mt-1">
                Ensure a proforma invoice has been created and approved for your deals or quotations first.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {readyForInvoiceMilestones.map(({ schedule, milestone, index }) => {
                const isSelected =
                  selectedScheduleForGen?._id === schedule._id &&
                  selectedMilestoneForGen?._id === milestone._id;

                return (
                  <div
                    key={`${schedule._id}-${milestone._id}`}
                    onClick={() => {
                      setSelectedScheduleForGen(schedule);
                      setSelectedMilestoneForGen(milestone);
                    }}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#6825F5] bg-[#EDE9FE]/50'
                        : 'border-[#E8E8F5] hover:border-[#C7D2FE]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-xs text-[#11183F]">
                        {schedule.clientName || 'Client'} — {milestone.name}
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#EDE9FE] text-[#6825F5] border border-purple-200">
                        {milestone.percentage}% ({formatINR(milestone.milestoneTotal || 0)})
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#747C9C] mt-1 font-mono">
                      {schedule.quotationNumber && <span>Quot: {schedule.quotationNumber}</span>}
                      {schedule.proformaNumber && <span>PI: {schedule.proformaNumber}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E8E8F5]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setGenerateModalOpen(false)}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerateInvoice}
              disabled={generating || !selectedScheduleForGen || !selectedMilestoneForGen}
              className="bg-[#6825F5] hover:bg-[#581cd6] text-white shadow-sm shadow-purple-200"
            >
              {generating ? <Spinner className="w-3.5 h-3.5 mr-1" /> : <PlusCircle className="w-3.5 h-3.5 mr-1" />}
              Generate Official Tax Invoice
            </Button>
          </div>
        </div>
      </Modal>

      {/* Record Payment Modal */}
      <Modal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        title={`Record Payment for ${paymentInvoice?.invoiceNumber}`}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1">
              Amount Received (₹)
            </label>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              className="w-full px-3 py-2 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] font-mono focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
            >
              <option value="bank_transfer">Bank Transfer (NEFT / RTGS / IMPS)</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="card">Credit / Debit Card</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1">
              Transaction / UTR Reference
            </label>
            <input
              type="text"
              placeholder="e.g. UTR12345678"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E8E8F5]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaymentModalOpen(false)}
              disabled={recordingPayment}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleRecordPaymentSubmit}
              disabled={recordingPayment || paymentAmount <= 0}
              className="bg-[#10B981] hover:bg-[#059669] text-white"
            >
              {recordingPayment ? <Spinner className="w-3.5 h-3.5 mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Confirm Payment
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject / Request Revision Modal */}
      <Modal
        open={rejectModalOpen}
        onClose={() => {
          if (!rejecting) {
            setRejectModalOpen(false);
            setRejectInvoiceTarget(null);
          }
        }}
        title={`Request Revision for Tax Invoice ${rejectInvoiceTarget?.invoiceNumber || ''}`}
        size="md"
      >
        <div className="space-y-4">
          <div className="p-3 bg-[#FFF7ED] border border-amber-200 rounded-lg text-xs text-amber-800">
            Returning this tax invoice will change its status to <strong>Revision Needed</strong> and alert the sales team with your feedback.
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1">
              Reason for Revision <span className="text-rose-500">*</span>
            </label>
            <select
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={rejecting}
              className="w-full px-3 py-2 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] focus:outline-none focus:ring-2 focus:ring-[#6825F5] focus:border-transparent"
            >
              <option value="Incorrect Tax / Amount Details">Incorrect Tax / Amount Details</option>
              <option value="Incorrect Client / GSTIN Information">Incorrect Client / GSTIN Information</option>
              <option value="Missing or Incomplete Deliverables">Missing or Incomplete Deliverables</option>
              <option value="Milestone Scope / Terms Changed">Milestone Scope / Terms Changed</option>
              <option value="Other Revision Required">Other Revision Required</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#11183F] mb-1">
              Revision Notes & Action Items <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              disabled={rejecting}
              rows={3}
              placeholder="Specify exactly what needs to be corrected or revised before this tax invoice can be approved..."
              className="w-full px-3 py-2 text-xs bg-[#F8F9FE] border border-[#E8E8F5] rounded-lg text-[#11183F] resize-none focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[#E8E8F5]">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRejectModalOpen(false);
                setRejectInvoiceTarget(null);
              }}
              disabled={rejecting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmitReject}
              disabled={rejecting || !rejectNote.trim()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {rejecting ? <Spinner className="w-3.5 h-3.5 mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
              Submit Revision Request
            </Button>
          </div>
        </div>
      </Modal>

      {/* Workspace Drawer for Preview / Edit / PDF */}
      <SalesDocumentWorkspaceDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedDocData(null);
        }}
        document={selectedDocData}
        readOnly
      />
    </div>
  );
}